import { StateEffect, StateField, Transaction } from "@codemirror/state";
import { gutter } from "@codemirror/view";
import { Slice } from "utils/helpers/Query/splitSQLQuery";

import MarkerSpacer from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/extensions/statementGutter/MarkerSpacer";
import StatementMarker from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/extensions/statementGutter/StatementMarker";

interface State {
  slice: Slice | null;
  pending: boolean;
}

export const StatementGutterMarkerEffect = StateEffect.define<State>();
export const StatementGutterMarkerPendingEffect = StateEffect.define<boolean>();

export const StatementGutterMarkerState: StateField<State> =
  StateField.define<State>({
    create: () => {
      return {
        slice: null,
        pending: false,
      };
    },
    update: (state: State, transaction: Transaction) => {
      for (const e of transaction.effects) {
        if (e.is(StatementGutterMarkerEffect)) {
          return e.value;
        }
        if (e.is(StatementGutterMarkerPendingEffect)) {
          return { ...state, pending: e.value };
        }
      }

      // Keep stored slice positions in sync with document edits
      if (transaction.docChanged && state.slice) {
        const mappedFrom = transaction.changes.mapPos(state.slice.pos[0], -1);
        const mappedTo = transaction.changes.mapPos(state.slice.pos[1], 1);

        // If the slice collapsed or became invalid, clear it
        if (mappedTo <= mappedFrom) {
          return { slice: null, pending: state.pending };
        }

        const updatedText = transaction.newDoc.sliceString(
          mappedFrom,
          mappedTo
        );
        return {
          slice: {
            ...state.slice,
            pos: [mappedFrom, mappedTo],
            text: updatedText,
          },
          pending: state.pending,
        };
      }

      return state;
    },
  });

export const findSliceAtPosition = (slices: Slice[], position: number) => {
  for (let i = 0; i < slices.length; i++) {
    const slice = slices[i];
    if (slice.pos[0] <= position && slice.pos[1] >= position) {
      return slice;
    }
  }
  return null;
};

export const StatementGutterMarkerDecorator = gutter({
  lineMarkerChange: update => {
    const hasStatementGutterEffect = update.transactions.some(tr =>
      tr.effects.some(e => e.is(StatementGutterMarkerEffect))
    );

    // Recompute markers when our effect runs or when the document changes
    return hasStatementGutterEffect || update.docChanged;
  },
  initialSpacer: () => {
    return new MarkerSpacer();
  },
  lineMarker(view, line) {
    const { slice } = view.state.field(StatementGutterMarkerState);
    if (!slice) return null;

    const isStatementOnLine =
      line.from >= slice.pos[0] && line.to <= slice.pos[1];
    const isStart = slice.pos[0] >= line.from && slice.pos[0] <= line.to;
    const isEnd = slice.pos[1] >= line.from && slice.pos[1] <= line.to;

    if (isStart || isEnd || isStatementOnLine) {
      return new StatementMarker(isStart, isEnd);
    }

    return null;
  },
});
